import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { MutationState } from 'src/app/shared/utils/async';

import type { CreateFreePromotionErrorCode } from '../../constants';
import { CREATE_FREE_PROMOTION_ERROR_MAP, createFreePromotionErrorCodes } from '../../constants';

import { createFreePromotion } from './api';

type RequestState = MutationState<{ data: { status: 'success'; id: string } }, { error: CreateFreePromotionErrorCode }>;

type UseFreePromotionParams = { id: string };
type UseFreePromotionOptions = {
	onSuccess?: (data: { status: 'success'; id: string }) => void;
	onError?: (error: CreateFreePromotionErrorCode) => void;
};

export type UseFreePromotionReturn = RequestState & {
	mutate: (
		params: UseFreePromotionParams,
	) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};
export function useCreateFreePromotion({ onSuccess, onError }: UseFreePromotionOptions = {}): UseFreePromotionReturn {
	const utilityContext = useUtilityContext();
	const [state, setState] = useState<RequestState>({ status: 'idle' });
	const handleCreateFreePromotion = useCallback(
		async ({ id }: UseFreePromotionParams) => {
			setState({ status: 'loading' });

			try {
				const result = await createFreePromotion(id, utilityContext);
				const successStatus = { status: 'loaded' as const, data: result };
				setState(successStatus);
				onSuccess?.(result);
				return successStatus;
			} catch (e) {
				const errorMessage = parseError(e);
				const errorStatus = { status: 'error' as const, error: errorMessage };
				setState(errorStatus);
				onError?.(errorMessage);
				return errorStatus;
			}
		},
		[onError, onSuccess, utilityContext],
	);
	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);
	return { mutate: handleCreateFreePromotion, reset: handleReset, ...state };
}
function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return createFreePromotionErrorCodes.find((val) => val === err) ?? CREATE_FREE_PROMOTION_ERROR_MAP.GENERIC_ERROR.code;
}
